import React, { useEffect, useState } from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "./ui/hover-card";
import { Sparkles, TrendingUp, Clock, Percent } from "lucide-react";
import { api } from "../lib/api";
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
} from "recharts";

// Simple cache to avoid refetching the same score on repeated hovers.
const _scoreCache = new Map();

const AXIS_LABELS = {
  margin: "Margine",
  position: "Posizione",
  legal: "Legale",
  documentation: "Documenti",
  duration: "Tempo",
  interest: "Interesse",
};

function scoreColor(v) {
  // Colors optimized for the dark signature header (lighter tones).
  if (v >= 75) return "text-emerald-400";
  if (v >= 50) return "text-sky-400";
  if (v >= 25) return "text-amber-400";
  return "text-rose-400";
}

function scoreLabel(v) {
  if (v >= 85) return "Eccellente";
  if (v >= 70) return "Buona";
  if (v >= 50) return "Discreta";
  return "Debole";
}

/**
 * Hover-only preview of the Opportunity Index for a property.
 * Wrap the trigger children (e.g. the property title cell) with this component.
 */
export default function PropertyHoverPreview({ propertyId, propertyTitle, address, city, children }) {
  const [score, setScore] = useState(() => _scoreCache.get(propertyId) || null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open || score || loading) return;
    setLoading(true);
    api.get(`/properties/${propertyId}/score`)
      .then(({ data }) => { _scoreCache.set(propertyId, data); setScore(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, propertyId, score, loading]);

  const radarData = score ? Object.entries(score.radar || {}).map(([k, v]) => ({ axis: AXIS_LABELS[k] || k, value: v })) : [];
  const total = score?.total ?? 0;
  const prob = score?.closing_probability ?? null; // already in %
  const days = score?.estimated_days ?? null;
  const margin = score?.margin_pct ?? null;
  const reasons = Array.isArray(score?.reasons) ? score.reasons : [];

  return (
    <HoverCard openDelay={200} closeDelay={80} open={open} onOpenChange={setOpen}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent side="right" align="start" className="w-96 p-0 overflow-hidden border-border/60 shadow-2xl" data-testid={`hover-preview-${propertyId}`}>
        {/* Header with dark signature */}
        <div className="bg-slate-900 dark:bg-slate-950 text-white p-4 relative">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400 mb-2">
            <Sparkles className="w-3 h-3" /> Opportunity Index
          </div>
          <div className="flex items-baseline gap-2">
            <div className={`font-heading text-4xl font-semibold tabular-nums ${score ? scoreColor(total) : "text-slate-500"}`}>
              {score ? total.toFixed(1) : "—"}
            </div>
            <div className="text-slate-400 text-sm">/100</div>
            {score && (
              <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/10 text-white">{scoreLabel(total)}</span>
            )}
          </div>
          <div className="mt-1 text-sm font-medium text-white truncate">{propertyTitle}</div>
          {(address || city) && (
            <div className="text-xs text-slate-400 truncate">
              {address}{city ? `, ${city}` : ""}
            </div>
          )}
        </div>

        {/* Body: mini radar + inline metrics */}
        <div className="p-4 bg-background">
          <div className="grid grid-cols-5 gap-3 items-center">
            <div className="col-span-2 h-32">
              {loading && !score && <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">Calcolo…</div>}
              {score && (
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData} outerRadius={44}>
                    <PolarGrid stroke="hsl(var(--border))" />
                    <PolarAngleAxis dataKey="axis" tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                    <Radar dataKey="value" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.35} />
                  </RadarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="col-span-3 space-y-2">
              <MiniRow icon={Percent} label="Prob. chiusura" value={prob != null ? `${prob.toFixed(0)}%` : "—"} />
              <MiniRow icon={Clock} label="Tempo stimato" value={days != null ? `${days}g` : "—"} />
              <MiniRow icon={TrendingUp} label="Margine" value={margin != null ? `${margin.toFixed(1)}%` : "—"} />
            </div>
          </div>
          {reasons.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border/60">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Perché</div>
              <ul className="space-y-1">
                {reasons.slice(0, 3).map((r, i) => (
                  <li key={i} className="text-xs text-foreground flex items-start gap-1.5"><span className="text-emerald-600 mt-0.5">✓</span>{r}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

function MiniRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
        {label}
      </span>
      <span className="tabular-nums font-medium">{value}</span>
    </div>
  );
}
